import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useParams } from 'react-router-dom';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import { normalizeTagVariable } from '../../Utils/tagUtils';
import { useWebSocket } from '../WebSocketContext';
import TagPreviewPopover from './TagPreviewPopover';

const TAG_PREVIEW_DELAY = 400;
const TAG_PREVIEW_CLOSE_DELAY = 150;
const TAG_PREVIEW_CACHE_TTL = 30 * 1000;
const TAG_PREVIEW_CACHE_MAX_ENTRIES = 500;
const TAG_PREVIEW_REQUEST_TIMEOUT = 10 * 1000;
const tagPreviewCache = new Map();
const tagPreviewRequests = new Set();
const tagPreviewRequestTimers = new Map();

const getTagPreviewKey = (caseid, tagName) => `${caseid}:${tagName.toLowerCase()}`;

const getCachedTagPreview = (cacheKey) => {
  const cached = tagPreviewCache.get(cacheKey);
  if (!cached) {
    return null;
  }
  if (cached.expiresAt <= Date.now()) {
    tagPreviewCache.delete(cacheKey);
    return null;
  }
  return cached.value;
};

const cacheTagPreview = (cacheKey, value) => {
  tagPreviewCache.delete(cacheKey);
  tagPreviewCache.set(cacheKey, {
    value,
    expiresAt: Date.now() + TAG_PREVIEW_CACHE_TTL,
  });
  while (tagPreviewCache.size > TAG_PREVIEW_CACHE_MAX_ENTRIES) {
    const oldestKey = tagPreviewCache.keys().next().value;
    if (oldestKey === undefined) {
      break;
    }
    tagPreviewCache.delete(oldestKey);
  }
};

const clearTagPreviewRequest = (cacheKey) => {
  if (!cacheKey) {
    return;
  }
  tagPreviewRequests.delete(cacheKey);
  const timer = tagPreviewRequestTimers.get(cacheKey);
  if (timer !== undefined) {
    clearTimeout(timer);
    tagPreviewRequestTimers.delete(cacheKey);
  }
};

const TagChip = ({
  tagName,
  onClick,
  sx = {},
  showIcon = true,
  iconColor,
  caseid = null,
  enablePreview = true
}) => {
  const { caseid: routeCaseId } = useParams();
  const { sendMessage, subscribeToMessages } = useWebSocket() || {};
  const previewCaseId = caseid || routeCaseId;
  const displayTagName = normalizeTagVariable(tagName);
  const bgColor = generatePastelColor(displayTagName);
  const textColor = displayTagName.startsWith('#') ? '#5757b0' : generateSaturatedColor(displayTagName);
  const chipRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const pendingPreviewKeyRef = useRef(null);
  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const [hoveredTag, setHoveredTag] = useState(null);
  const [hoverLoading, setHoverLoading] = useState(false);

  const closePreview = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHoverAnchorEl(null);
    setHoveredTag(null);
    setHoverLoading(false);
    pendingPreviewKeyRef.current = null;
  }, []);

  const scheduleClosePreview = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(closePreview, TAG_PREVIEW_CLOSE_DELAY);
  }, [closePreview]);

  const handleMouseEnter = useCallback((event) => {
    if (!enablePreview || !previewCaseId || !displayTagName || typeof sendMessage !== 'function') {
      return;
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    const anchor = event.currentTarget;
    const cacheKey = getTagPreviewKey(previewCaseId, displayTagName);
    setHoverAnchorEl(anchor);
    setHoveredTag(null);
    setHoverLoading(true);

    hoverTimerRef.current = setTimeout(() => {
      const cached = getCachedTagPreview(cacheKey);
      if (cached) {
        setHoveredTag(cached);
        setHoverLoading(false);
        pendingPreviewKeyRef.current = null;
        return;
      }

      setHoveredTag({ tagName: displayTagName, loading: true });
      pendingPreviewKeyRef.current = cacheKey;
      if (!tagPreviewRequests.has(cacheKey)) {
        tagPreviewRequests.add(cacheKey);
        const requestTimer = setTimeout(() => {
          clearTagPreviewRequest(cacheKey);
        }, TAG_PREVIEW_REQUEST_TIMEOUT);
        tagPreviewRequestTimers.set(cacheKey, requestTimer);
        try {
          sendMessage({ command: 'getTagPreview', caseid: previewCaseId, variable: displayTagName });
        } catch (error) {
          clearTagPreviewRequest(cacheKey);
        }
      }
    }, TAG_PREVIEW_DELAY);
  }, [displayTagName, enablePreview, previewCaseId, sendMessage]);

  useEffect(() => {
    if (!enablePreview || !previewCaseId || typeof subscribeToMessages !== 'function') {
      return undefined;
    }

    const unsubscribe = subscribeToMessages(['getTagPreview'], (message) => {
      const data = message.data || {};
      if (data.status === 'error' || data.error) {
        const responseVariable = data.variable
          ? normalizeTagVariable(data.variable)
          : '';
        const errorKey = responseVariable
          ? getTagPreviewKey(data.caseid || previewCaseId, responseVariable)
          : pendingPreviewKeyRef.current;
        clearTagPreviewRequest(errorKey);
        if (pendingPreviewKeyRef.current === errorKey) {
          pendingPreviewKeyRef.current = null;
        }
        return;
      }
      if (data.status !== 'success' || !data.variable) {
        return;
      }

      if (previewCaseId && data.caseid && String(data.caseid) !== String(previewCaseId)) {
        return;
      }

      const normalizedVariable = normalizeTagVariable(data.variable);
      const cacheKey = getTagPreviewKey(data.caseid || previewCaseId, normalizedVariable);
      const previewData = {
        tagName: normalizedVariable,
        tag_type: data.tag_type,
        label: data.label || normalizedVariable,
        content: data.content || '',
        type: String(data.type || 'text').trim().toLowerCase() || 'text',
        samples: data.samples || [],
        totalCount: data.totalCount || 0
      };

      clearTagPreviewRequest(cacheKey);
      cacheTagPreview(cacheKey, previewData);
      setHoveredTag((previousTag) => {
        if (previousTag?.tagName?.toLowerCase() === normalizedVariable.toLowerCase()) {
          setHoverLoading(false);
          return previewData;
        }
        return previousTag;
      });
    });

    return unsubscribe;
  }, [enablePreview, previewCaseId, subscribeToMessages]);

  useEffect(() => () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    pendingPreviewKeyRef.current = null;
  }, []);

  return (
    <>
      <Box
        component="span"
        className="tag-chip"
        data-tag={displayTagName}
        ref={chipRef}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={scheduleClosePreview}
        sx={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '12px',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          marginRight: '6px',
          backgroundColor: bgColor,
          color: textColor,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': onClick ? {
            opacity: 0.8,
            transform: 'scale(1.05)'
          } : {},
          ...sx
        }}
      >
        {showIcon && (
          <LocalOfferIcon
            sx={{
              fontSize: '0.8rem',
              color: iconColor || textColor,
              marginRight: '4px',
              verticalAlign: 'middle'
            }}
          />
        )}
        {displayTagName}
      </Box>
      <TagPreviewPopover
        anchorEl={hoverAnchorEl}
        tag={hoveredTag}
        loading={hoverLoading}
        onClose={closePreview}
        onMouseEnter={() => {
          if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
          }
        }}
        onMouseLeave={closePreview}
      />
    </>
  );
};

export default TagChip;
